import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, RefreshCw, Link2, CheckSquare, Wand2, Save, Trash2, CheckCircle2, XCircle } from 'lucide-react';
import {
  fetchSedesTextoSinCodigo, fetchSedesTextoMapping,
  saveSedeTextoMapping, bulkSaveSedeTextoMapping, deleteSedeTextoMapping,
  fetchSedesFuzzySuggest,
} from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export function MapeoSedesTab() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [pendingCodes, setPendingCodes] = useState({});
  const [savingItem, setSavingItem] = useState(null);
  const [suggesting, setSuggesting] = useState(null);
  const [bulkSuggesting, setBulkSuggesting] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  // validation: { [texto]: { checking: bool, valid: bool|null } }
  const [validation, setValidation] = useState({});

  const { data: sinCodigo = [], isLoading } = useQuery({
    queryKey: ['sedes-texto-sin-codigo'],
    queryFn: fetchSedesTextoSinCodigo,
  });
  const { data: savedMappings = [] } = useQuery({
    queryKey: ['sedes-texto-mapping'],
    queryFn: fetchSedesTextoMapping,
  });

  const filtered = sinCodigo.filter(s =>
    s.texto.toLowerCase().includes(search.toLowerCase())
  );

  // Cuántos pendingCodes tienen código válido
  const pendingWithCode = Object.entries(pendingCodes).filter(([, v]) => v?.code);

  const validateCode = async (texto, code) => {
    const trimmed = code.trim();
    if (!trimmed) { setValidation(prev => ({ ...prev, [texto]: { valid: null } })); return; }
    setValidation(prev => ({ ...prev, [texto]: { checking: true } }));
    try {
      const tok = localStorage.getItem('valoriza_token');
      const res = await axios.get(`${API}/api/admin/centros-catalog/${encodeURIComponent(trimmed)}`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      // Auto-fill label if empty
      if (res.data?.nombre && !pendingCodes[texto]?.label) {
        setPendingCodes(prev => ({ ...prev, [texto]: { ...prev[texto], label: res.data.nombre } }));
      }
      setValidation(prev => ({ ...prev, [texto]: { valid: true } }));
    } catch {
      setValidation(prev => ({ ...prev, [texto]: { valid: false } }));
    }
  };

  const handleSuggest = async (texto) => {
    setSuggesting(texto);
    try {
      const suggestions = await fetchSedesFuzzySuggest(texto);
      if (!suggestions.length) { toast.warning(`Sin sugerencias para "${texto}"`); return; }
      const best = suggestions[0];
      setPendingCodes(prev => ({ ...prev, [texto]: { code: best.code, label: best.label } }));
      setValidation(prev => ({ ...prev, [texto]: { valid: true } }));
      toast.info(`Sugerencia: ${best.code} — ${best.label} (coincidencia ${best.score}%)`);
    } catch { toast.error('Error al obtener sugerencia'); }
    finally { setSuggesting(null); }
  };

  const handleSuggestAll = async () => {
    const pending = filtered.filter(r => !pendingCodes[r.texto]?.code);
    if (!pending.length) { toast.info('Todas las filas ya tienen código pendiente'); return; }
    setBulkSuggesting(true);
    try {
      const results = await Promise.all(
        pending.map(r => fetchSedesFuzzySuggest(r.texto).then(s => ({ texto: r.texto, s })).catch(() => ({ texto: r.texto, s: [] })))
      );
      const updates = {};
      let filled = 0;
      results.forEach(({ texto, s }) => {
        if (s.length) { updates[texto] = { code: s[0].code, label: s[0].label }; filled++; }
      });
      setPendingCodes(prev => ({ ...prev, ...updates }));
      // Mark all auto-suggested as valid (they come from fuzzy match against catalog)
      setValidation(prev => ({
        ...prev,
        ...Object.fromEntries(Object.keys(updates).map(t => [t, { valid: true }])),
      }));
      toast.success(`${filled} sugerencias aplicadas — revísalas antes de guardar`);
    } catch { toast.error('Error en autocompletar'); }
    finally { setBulkSuggesting(false); }
  };

  const handleSave = async (texto) => {
    const pending = pendingCodes[texto] || {};
    if (!pending.code) { toast.error('Introduce el código'); return; }
    // Not being in centros_catalog yet is just a heads-up, not a hard block —
    // Mapeo Sedes assigns a code to a text; cataloguing that code (name,
    // gerente, delegación) is a separate step. Codes like "Z513900"/"H310001"
    // (SAP plant codes) are valid even before they have full catalog metadata.
    if (validation[texto]?.valid === false) {
      toast.warning(`El código "${pending.code}" aún no está en el catálogo — se guarda el alias igualmente.`);
    }
    setSavingItem(texto);
    try {
      const res = await saveSedeTextoMapping({ texto, code: pending.code.trim(), label: pending.label?.trim() || pending.code.trim() });
      toast.success(`Mapeado: "${texto}" → ${pending.code} | ${res.updated_ops} OPS + ${res.updated_lid} Liderazgo actualizados`);
      setPendingCodes(prev => { const n = { ...prev }; delete n[texto]; return n; });
      qc.invalidateQueries({ queryKey: ['sedes-texto-sin-codigo'] });
      qc.invalidateQueries({ queryKey: ['sedes-texto-mapping'] });
    } catch { toast.error('Error al guardar el mapeo'); }
    finally { setSavingItem(null); }
  };

  const handleSaveAll = async () => {
    if (!pendingWithCode.length) { toast.info('No hay códigos pendientes para guardar'); return; }
    setBulkSaving(true);
    try {
      const items = pendingWithCode.map(([texto, v]) => ({
        texto,
        code: v.code.trim(),
        label: v.label?.trim() || v.code.trim(),
      }));
      const res = await bulkSaveSedeTextoMapping(items);
      toast.success(`${res.saved} mapeos guardados · ${res.updated_ops} OPS + ${res.updated_lid} Liderazgo actualizados`);
      setPendingCodes({});
      qc.invalidateQueries({ queryKey: ['sedes-texto-sin-codigo'] });
      qc.invalidateQueries({ queryKey: ['sedes-texto-mapping'] });
    } catch { toast.error('Error al guardar en bloque'); }
    finally { setBulkSaving(false); }
  };

  const handleDelete = async (texto) => {
    await deleteSedeTextoMapping(texto);
    toast.success('Mapeo eliminado');
    qc.invalidateQueries({ queryKey: ['sedes-texto-mapping'] });
  };

  const totalAffected = sinCodigo.reduce((s, r) => s + r.total, 0);

  return (
    <div className="space-y-5" data-testid="mapeo-sedes-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold" style={{ color: 'var(--vz-text-primary)' }}>Sedes sin código</h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
            Nombres de texto en ArcGIS aún sin código asignado (numérico o SAP tipo Z/H). Los que ya
            tienen alias en Mapeo Texto se resuelven solos y desaparecen de esta lista.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Stats */}
          {[
            { value: sinCodigo.length, label: 'Sin resolver', color: '#F59E0B' },
            { value: totalAffected, label: 'Registros afectados', color: '#EF4444' },
            { value: savedMappings.length, label: 'Resueltos', color: '#22C55E' },
          ].map(({ value, label, color }) => (
            <div key={label} className="rounded-xl border px-4 py-2 text-center" style={{ borderColor: 'var(--vz-border-subtle)', background: 'var(--vz-bg-elevated)' }}>
              <p className="text-xl font-bold" style={{ color }}>{value}</p>
              <p className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>{label}</p>
            </div>
          ))}

          {/* Sugerencia automática */}
          <Button
            size="sm"
            onClick={handleSuggestAll}
            disabled={bulkSuggesting || filtered.length === 0}
            className="flex items-center gap-1.5 self-center px-4"
            style={{ background: 'linear-gradient(135deg,#8B5CF6,#6D28D9)', color: '#fff', border: 'none' }}
            data-testid="mapeo-suggest-all-btn"
            title="Autocompletar sugerencias para todas las filas visibles"
          >
            {bulkSuggesting ? <RefreshCw size={13} className="animate-spin" /> : <Wand2 size={13} />}
            {bulkSuggesting ? 'Analizando...' : 'Sugerencia automática'}
          </Button>

          {/* Guardar todo */}
          {pendingWithCode.length > 0 && (
            <Button
              size="sm"
              onClick={handleSaveAll}
              disabled={bulkSaving}
              className="flex items-center gap-1.5 self-center px-4"
              style={{ background: 'linear-gradient(135deg,#059669,#10B981)', color: '#fff', border: 'none' }}
              data-testid="mapeo-save-all-btn"
              title={`Guardar todos los ${pendingWithCode.length} mapeos pendientes de una vez`}
            >
              {bulkSaving ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
              {bulkSaving ? 'Guardando...' : `Guardar todo (${pendingWithCode.length})`}
            </Button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--vz-text-muted)' }} />
        <Input
          className="pl-9 h-9 text-sm"
          placeholder="Buscar por nombre de sede..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          data-testid="mapeo-sedes-search"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12" style={{ color: 'var(--vz-text-muted)' }}>
          <CheckSquare size={40} className="mx-auto mb-3 opacity-40" />
          <p className="font-medium">¡Todo resuelto!</p>
          <p className="text-xs mt-1">No hay sedes pendientes de mapeo</p>
        </div>
      ) : (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--vz-border-subtle)' }}>
          <Table>
            <TableHeader>
              <TableRow style={{ background: 'var(--vz-bg-elevated)' }}>
                <TableHead className="text-xs font-semibold w-5/12">Nombre en ArcGIS</TableHead>
                <TableHead className="text-xs font-semibold text-center w-[6%]">OPS</TableHead>
                <TableHead className="text-xs font-semibold text-center w-[8%]">Liderazgo</TableHead>
                <TableHead className="text-xs font-semibold w-[13%]">Código</TableHead>
                <TableHead className="text-xs font-semibold w-[18%]">Etiqueta (opcional)</TableHead>
                <TableHead className="text-xs font-semibold w-[5%]" title="Sugerencia fuzzy"></TableHead>
                <TableHead className="text-xs font-semibold w-[5%]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((row) => (
                <TableRow key={row.texto} className="text-sm" data-testid={`mapeo-row-${row.texto}`}>
                  <TableCell>
                    <code className="text-xs px-1.5 py-0.5 rounded font-mono break-all"
                      style={{ background: 'var(--vz-bg-elevated)', color: 'var(--vz-text-secondary)' }}>
                      {row.texto}
                    </code>
                  </TableCell>
                  <TableCell className="text-center">
                    {row.count_ops > 0
                      ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#1E40AF22', color: '#60A5FA' }}>{row.count_ops}</span>
                      : <span className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>—</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {row.count_lid > 0
                      ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#15803D22', color: '#4ADE80' }}>{row.count_lid}</span>
                      : <span className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>—</span>}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Input
                        className="h-7 text-xs font-mono"
                        placeholder="ej. 5000403"
                        value={(pendingCodes[row.texto]?.code) || ''}
                        onChange={e => {
                          setPendingCodes(prev => ({ ...prev, [row.texto]: { ...prev[row.texto], code: e.target.value } }));
                          setValidation(prev => ({ ...prev, [row.texto]: { valid: null } }));
                        }}
                        onBlur={e => { if (e.target.value.trim()) validateCode(row.texto, e.target.value); }}
                        data-testid={`mapeo-code-input-${row.texto}`}
                      />
                      {validation[row.texto]?.checking && (
                        <RefreshCw size={12} className="animate-spin flex-shrink-0" style={{ color: '#6b7280' }} />
                      )}
                      {!validation[row.texto]?.checking && validation[row.texto]?.valid === true && (
                        <CheckCircle2 size={13} className="flex-shrink-0" style={{ color: '#22C55E' }} title="Código válido en catálogo" />
                      )}
                      {!validation[row.texto]?.checking && validation[row.texto]?.valid === false && (
                        <XCircle size={13} className="flex-shrink-0" style={{ color: '#EF4444' }} title="Código no encontrado en catálogo" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Input
                      className="h-7 text-xs"
                      placeholder="Nombre legible"
                      value={(pendingCodes[row.texto]?.label) || ''}
                      onChange={e => setPendingCodes(prev => ({ ...prev, [row.texto]: { ...prev[row.texto], label: e.target.value } }))}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm" variant="ghost" className="h-7 w-7 p-0"
                      disabled={suggesting === row.texto}
                      onClick={() => handleSuggest(row.texto)}
                      data-testid={`mapeo-suggest-btn-${row.texto}`}
                      title="Sugerencia automática por similitud de nombre"
                      style={{ color: '#8B5CF6' }}
                    >
                      {suggesting === row.texto ? <RefreshCw size={12} className="animate-spin" /> : <Wand2 size={12} />}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm" className="h-7 px-3 text-xs gap-1"
                      disabled={savingItem === row.texto || !pendingCodes[row.texto]?.code || validation[row.texto]?.valid === false}
                      onClick={() => handleSave(row.texto)}
                      data-testid={`mapeo-save-btn-${row.texto}`}
                      style={{
                        background: (pendingCodes[row.texto]?.code && validation[row.texto]?.valid !== false)
                          ? (validation[row.texto]?.valid === true ? '#16A34A' : '#4A9EFF')
                          : undefined,
                      }}
                      title={validation[row.texto]?.valid === false ? 'Código no válido — verifica en catálogo' : undefined}
                    >
                      {savingItem === row.texto ? <RefreshCw size={12} className="animate-spin" /> : <Link2 size={12} />}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Saved mappings */}
      {savedMappings.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--vz-text-secondary)' }}>
            <CheckSquare size={14} style={{ color: '#22C55E' }} />
            Mapeos guardados ({savedMappings.length})
          </h3>
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--vz-border-subtle)' }}>
            <Table>
              <TableHeader>
                <TableRow style={{ background: 'var(--vz-bg-elevated)' }}>
                  <TableHead className="text-xs">Texto original</TableHead>
                  <TableHead className="text-xs">Código</TableHead>
                  <TableHead className="text-xs">Etiqueta</TableHead>
                  <TableHead className="text-xs">Por</TableHead>
                  <TableHead className="text-xs w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {savedMappings.map(m => (
                  <TableRow key={m.texto} className="text-xs">
                    <TableCell><code className="font-mono text-xs break-all" style={{ color: 'var(--vz-text-muted)' }}>{m.texto}</code></TableCell>
                    <TableCell><span className="font-semibold" style={{ color: '#4A9EFF' }}>{m.code}</span></TableCell>
                    <TableCell style={{ color: 'var(--vz-text-secondary)' }}>{m.label}</TableCell>
                    <TableCell style={{ color: 'var(--vz-text-muted)' }}>{m.updated_by}</TableCell>
                    <TableCell>
                      <button onClick={() => handleDelete(m.texto)} className="opacity-50 hover:opacity-100 transition-opacity">
                        <Trash2 size={13} style={{ color: '#EF4444' }} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
